// ============================================================================
// Impersonation fetch wrapper
// ============================================================================
// Installs a single global fetch interceptor that, WHILE an admin is
// impersonating a brand, attaches the admin's identity (X-Impersonator-*) to
// every request bound for the worker. The worker records it into the brand
// audit log's metadata (see worker/src/audit.ts), so admin-performed actions
// are attributed to the admin instead of looking like the brand did them.
//
// Why a global interceptor rather than editing each call site: worker calls are
// made both through src/lib/api.ts (workerFetch) AND via many inline fetches
// across pages/components. One choke point covers them all. It is tightly
// scoped — only requests to the worker host are touched, and only when an
// impersonation session is active — so Supabase and third-party calls are never
// modified.
// ============================================================================

import { getImpersonator } from './impersonation'
import { WORKER_URL } from './env'


function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return (input as Request).url
}

// True for requests headed to the Brand-Dashboard worker (both the absolute
// VITE_WORKER_URL form and the dev-proxied relative "/api" form).
function isWorkerRequest(url: string): boolean {
  if (WORKER_URL && url.startsWith(WORKER_URL)) return true
  if (url.startsWith('/api/') || url.startsWith('/api?')) return true
  return false
}

let installed = false

export function installImpersonationFetch(): void {
  if (installed || typeof window === 'undefined' || !window.fetch) return
  installed = true

  const originalFetch = window.fetch.bind(window)

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const imp = getImpersonator()
    if (imp && (imp.email || imp.id) && isWorkerRequest(resolveUrl(input))) {
      const headers = new Headers(
        init?.headers ?? (input instanceof Request ? input.headers : undefined)
      )
      if (imp.email) headers.set('X-Impersonator-Email', imp.email)
      if (imp.id) headers.set('X-Impersonator-Id', imp.id)

      // Request objects carry their own headers, so rebuild from the Request to
      // preserve method/body while overriding headers.
      if (input instanceof Request && !init) {
        return originalFetch(new Request(input, { headers }))
      }
      return originalFetch(input, { ...init, headers })
    }
    return originalFetch(input as RequestInfo, init)
  }
}
